import React from 'react';
import {
  Button,
  buttonSizes,
  buttonTypes,
  buttonTextSizes,
} from '../shared/Button';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FormattedMessage } from 'react-intl';
import SvgDownArrowIcon from '../svg/DownArrowIcon';
import SvgUpArrowIcon from '../svg/UpArrowIcon';
import SvgCompactDoubleArrowIcon from '../svg/SvgCompactDoubleArrowIcon';
import { resetText } from '../shared/SharedInternationalizations';

/**
 * Component to generate the filter bar for the list of messages
 * @param {object} props
 * @description - Takes in the filter status from the message list component and displays the filter and sort options appropriately. Includes date pickers for the start and end dates for filtering as well as checkboxes for filters and sort buttons for sort parameters
 */
function MessagesMessageListFilterBar(props) {
  const {
    currentFilters,
    language,
    filterMenuExpanded,
    setSortType,
    filterMenuToggle,
    setFilterFromDate,
    setFilterToDate,
    setFilterType,
    resetFilters,
  } = props;
  const {
    sortOrder,
    sortParam,
    dateTo,
    dateFrom,
    completed,
    cancelled,
    cleared,
    active,
  } = currentFilters;
  let titlerightIcon;
  if (sortParam === 'title') {
    if (sortOrder === 'az') {
      titlerightIcon = <SvgUpArrowIcon data-test="title-up" />;
    } else {
      titlerightIcon = <SvgDownArrowIcon data-test="title-down" />;
    }
  }

  let daterightIcon;
  if (sortParam === 'startTime') {
    if (sortOrder === 'az') {
      daterightIcon = <SvgUpArrowIcon data-test="start-time-up" />;
    } else {
      daterightIcon = <SvgDownArrowIcon data-test="start-time-down" />;
    }
  }

  let recipientrightIcon;
  if (sortParam === 'recipientCount') {
    if (sortOrder === 'az') {
      recipientrightIcon = <SvgUpArrowIcon data-test="recipient-count-up" />;
    } else {
      recipientrightIcon = (
        <SvgDownArrowIcon data-test="recipient-count-down" />
      );
    }
  }

  // This is used by the date pickers to set the newest date available to select
  let today = new Date();
  if (dateTo) {
    today = new Date(dateTo);
  }

  const sortText = (
    <FormattedMessage
      id="messageCenter.sortText"
      defaultMessage="Sort"
      description="Help text."
    />
  );
  const titleText = (
    <FormattedMessage
      id="messageCenter.titleText"
      defaultMessage="Title"
      description="Help text."
    />
  );
  const startTimeText = (
    <FormattedMessage
      id="messageCenter.startTimeText"
      defaultMessage="Start Time"
      description="Help text."
    />
  );
  const recipientsText = (
    <FormattedMessage
      id="messageCenter.recipientsText"
      defaultMessage="Recipients"
      description="Help text."
    />
  );
  const filtersText = (
    <FormattedMessage
      id="messageCenter.filtersText"
      defaultMessage="Filters"
      description="Help text."
    />
  );

  return (
    <div
      data-test="message-list-filter-bar-container"
      className={
        filterMenuExpanded
          ? 'message-list__sort-filter-container table__header message-list__filter-container--expanded'
          : 'message-list__sort-filter-container table__header '
      }>
      <div className="message-list__sort-container">
        <span className="text-bold text-medium">{sortText}:</span>
        <Button
          onClick={setSortType}
          text={titleText}
          rightIcon={titlerightIcon}
          buttonType={buttonTypes.noButton}
          buttonSize={buttonSizes.small}
          value="title"
          data-test="title-button"
          buttonStyle={
            'text-medium ' +
            (sortParam === 'title'
              ? 'text-extra-bold text-very-light-grey'
              : 'text-regular text-white')
          }
        />
        <Button
          onClick={setSortType}
          text={startTimeText}
          rightIcon={daterightIcon}
          buttonType={buttonTypes.noButton}
          buttonSize={buttonSizes.small}
          value="startTime"
          data-test="start-time-button"
          buttonStyle={
            'text-medium ' +
            (sortParam === 'startTime'
              ? 'text-extra-bold text-very-light-grey'
              : 'text-regular text-white')
          }
        />
        <Button
          onClick={setSortType}
          text={recipientsText}
          rightIcon={recipientrightIcon}
          buttonType={buttonTypes.noButton}
          buttonSize={buttonSizes.small}
          value="recipientCount"
          data-test="recipient-count-button"
          buttonStyle={
            'text-medium ' +
            (sortParam === 'recipientCount'
              ? 'text-extra-bold text-very-light-grey'
              : 'text-regular text-white')
          }
        />
        <Button
          onClick={filterMenuToggle}
          text={filtersText}
          rightIcon={<SvgCompactDoubleArrowIcon />}
          buttonType={buttonTypes.secondary}
          buttonSize={buttonSizes.extraSmall}
          data-test="filters-button"
          value="filters"
          rightIconStyle="margin-left-5 text-small"
        />
      </div>
      <div className="message-list__filter-container">
        <div className="message-list__filter-title">
          <FormattedMessage
            id="messagesMessageList.listFilterTitle"
            defaultMessage="Filters"
            description="The title for the filters to filter the list by">
            {(txt) => <span className="text-large text-bold">{txt}</span>}
          </FormattedMessage>
        </div>
        <div className="message-list__date-filter-container">
          <FormattedMessage
            id="messagesMessageList.listFilterStartDateLable"
            defaultMessage="Start Date:"
            description="The start date to filter messages by date">
            {(txt) => (
              <span className="text-bold text-large message-list__date-filter-label">
                {txt}
              </span>
            )}
          </FormattedMessage>
          
          <DatePicker
            selected={dateFrom}
            onChange={setFilterFromDate}
            maxDate={today}
            dateFormat="yyyy-MM-dd"
            data-test="date-from-picker"
            className="js-date-from"
          />

          <FormattedMessage
            id="messagesMessageList.listFilterEndDateLable"
            defaultMessage="End Date:"
            description="The end date to filter messages by date">
            {(txt) => (
              <span className="text-bold text-large message-list__date-filter-label">
                {txt}
              </span>
            )}
          </FormattedMessage>

          <DatePicker
            selected={dateTo}
            onChange={setFilterToDate}
            maxDate={today}
            dateFormat="yyyy-MM-dd"
            data-test="date-to-picker"
            className="js-date-to"
          />
        </div>
        <div className="message-list__filter-option">
          <input
            type="checkbox"
            id="active"
            name="active"
            onChange={setFilterType}
            checked={active}
            className="margin-left-5"
          />
          <FormattedMessage
            id="messagesMessageList.listFilterActive"
            defaultMessage="Active"
            description="Filter by messages that are active">
            {(txt) => (
              <label htmlFor="active" className="margin-left-5">
                {txt}
              </label>
            )}
          </FormattedMessage>
        </div>
        <div className="message-list__filter-option">
          <input
            type="checkbox"
            id="completed"
            name="completed"
            onChange={setFilterType}
            checked={completed}
            className="margin-left-5"
            data-test="completed-checkbox"
          />
          <FormattedMessage
            id="messagesMessageList.listFilterCompleted"
            defaultMessage="Completed"
            description="Filter by messages that are completed">
            {(txt) => (
              <label htmlFor="completed" className="margin-left-5">
                {txt}
              </label>
            )}
          </FormattedMessage>
        </div>
        <div className="message-list__filter-option">
          <input
            type="checkbox"
            id="cancelled"
            name="cancelled"
            onChange={setFilterType}
            checked={cancelled}
            className="margin-left-5"
            data-test="cancelled-checkbox"
          />
          <FormattedMessage
            id="messagesMessageList.listFilterCancelled"
            defaultMessage="Cancelled"
            description="Filter by messages that are cancelled">
            {(txt) => (
              <label htmlFor="cancelled" className="margin-left-5">
                {txt}
              </label>
            )}
          </FormattedMessage>
        </div>
        <div className="message-list__filter-option">
          <input
            type="checkbox"
            id="cleared"
            name="cleared"
            onChange={setFilterType}
            checked={cleared}
            className="margin-left-5"
            data-test="cleared-checkbox"
          />
          <FormattedMessage
            id="messagesMessageList.listFilterCleared"
            defaultMessage="Cleared"
            description="Filter by messages that are cleared">
            {(txt) => (
              <label htmlFor="cleared" className="margin-left-5">
                {txt}
              </label>
            )}
          </FormattedMessage>
        </div>
        <div className="message-list__filter-reset">
          <Button
            onClick={resetFilters}
            buttonType={buttonTypes.secondary}
            buttonSize={buttonSizes.extraSmall}
            text={resetText}
            buttonTextSize={buttonTextSizes.small}
            additionalStyles="no-margin"
            data-test="reset-button"
          />
        </div>
      </div>
    </div>
  );
}

export default MessagesMessageListFilterBar;
